import { useEffect, useState } from 'react';

import { describeApiError } from '../../lib/api-client';
import { useApiClient } from '../../providers/auth-provider';
import {
  useProxyVaultConnection,
  useProxyVaultRevisions,
  useSpecPreview,
} from '../../hooks/use-proxy-vault';

import { TrafficPane } from './TrafficPane';
import { ValuesPage } from './ValuesPage';
import {
  Card,
  Empty,
  Metric,
  Pill,
  StatusDot,
  Tabs,
  bindingRollUp,
  connectionLabel,
  fmtWhen,
  mono,
  primaryButton,
  secondaryButton,
} from './ui';

/**
 * One connection, as its own workspace tab.
 *
 * Four pages, and the split follows the four things a connection actually is:
 * a status, a document, a ledger of values, and its slice of the traffic. A
 * popover could hold none of that, which is why a row opens a tab.
 */
export function ConnectionView({
  connectionId,
  tenantId,
}: {
  connectionId: string | undefined;
  tenantId: string | undefined;
}) {
  const [page, setPage] = useState<'overview' | 'spec' | 'values' | 'traffic'>('overview');
  const { connection, status, error, reload, setConnection } = useProxyVaultConnection(connectionId);

  if (!connectionId) return <Empty>No connection selected.</Empty>;
  if (status === 'error') return <Empty>{error ?? 'Connection not found.'}</Empty>;
  if (!connection) return <Empty>Loading…</Empty>;

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--bg)' }}>
      <div
        style={{
          padding: '14px 20px 10px',
          display: 'flex',
          alignItems: 'baseline',
          gap: 10,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ fontSize: 14, fontWeight: 600 }}>⚿ {connectionLabel(connection)}</div>
        <div style={{ fontSize: 11, color: 'var(--text-soft)', fontFamily: mono }}>
          {/* When a label is set, the identifier still shows — it is what the
              Spec tab, the export and every audit row are keyed on, and an
              operator comparing the two needs both in front of them. */}
          {connection.display_name ? `${connection.name} · ` : ''}spec v{connection.spec_version} · org{' '}
          {connection.tenant_external_id ?? connection.tenant_name ?? connection.tenant_id}
        </div>
        {connection.materialization === 'cleartext' && <Pill tone="warn">cleartext</Pill>}
      </div>

      <Tabs
        tabs={[
          { id: 'overview', label: 'Overview' },
          { id: 'spec', label: 'Spec' },
          { id: 'values', label: 'Values' },
          { id: 'traffic', label: 'Traffic' },
        ]}
        active={page}
        onChange={(id) => setPage(id as typeof page)}
      />

      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {page === 'overview' && <OverviewPage connection={connection} />}
        {page === 'spec' && (
          <SpecPage
            connectionId={connectionId}
            tenantId={connection.tenant_id}
            specVersion={connection.spec_version}
            onApplied={(next) => {
              setConnection(next);
              reload();
            }}
          />
        )}
        {page === 'values' && (
          <ValuesPage
            connectionId={connectionId}
            connection={connection}
            {...(tenantId !== undefined && { actingTenantId: tenantId })}
          />
        )}
        {page === 'traffic' && <TrafficPane connectionId={connectionId} />}
      </div>
    </div>
  );
}

function OverviewPage({
  connection,
}: {
  connection: {
    id: string;
    status: string;
    visibility: string;
    materialization: string;
    hosts: string[];
    env_names: string[];
    captures: string[];
    unbound: string[];
    bound_organizations: number;
    bound_descendants: number;
    visible_organizations: number;
    created_at: string;
    updated_at: string;
    spec_version: number;
  };
}) {
  const { revisions } = useProxyVaultRevisions(connection.id);
  const rollUp = bindingRollUp(connection);

  return (
    <div style={{ padding: 20, overflowY: 'auto', height: '100%' }} data-testid="pv-connection-overview">
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
        <Card title="Status">
          <div style={{ marginBottom: 6 }}>
            <StatusDot status={connection.status} />
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-soft)' }}>
            {connection.unbound.length === 0
              ? 'all values bound'
              : `${connection.unbound.join(', ')} to bind`}
          </div>
          {rollUp && (
            <div style={{ fontSize: 11, color: 'var(--text-soft)', marginTop: 4 }} data-testid="pv-overview-rollup">
              {rollUp.text} bound
            </div>
          )}
        </Card>
        <Card title="Custody">
          <Metric value={connection.env_names.length} label="in custody" />
          <div style={{ fontSize: 11, color: 'var(--text-soft)' }}>
            {connection.env_names.length === 1 ? 'entry' : 'entries'} stamped into the cell
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-soft)' }}>
            {connection.captures.length} runtime capture{connection.captures.length === 1 ? '' : 's'}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-soft)', marginTop: 4 }}>
            {connection.visibility === 'children' ? 'visible to children' : 'private to this org'}
          </div>
        </Card>
        <Card title="Reach">
          {connection.hosts.map((h) => (
            <div key={h} style={{ fontFamily: mono, fontSize: 11 }}>
              {h}
            </div>
          ))}
        </Card>
      </div>

      <div style={{ fontSize: 11, color: 'var(--text-soft)', marginBottom: 6 }}>
        Environment variables stamped
      </div>
      <div style={{ fontFamily: mono, fontSize: 12, marginBottom: 20 }}>
        {connection.env_names.join('  ·  ') || '—'}
      </div>

      <div style={{ fontSize: 11, color: 'var(--text-soft)', marginBottom: 6 }}>Recent revisions</div>
      {revisions.slice(0, 6).map((r) => (
        <div key={r.version} style={{ fontSize: 11, marginBottom: 3 }}>
          <span style={{ fontFamily: mono, color: 'var(--text-soft)' }}>v{r.version}</span>{' '}
          <span style={{ color: 'var(--text-mute)' }}>{fmtWhen(r.created_at)}</span>{' '}
          {r.security_relevant && <Pill tone="warn">⚠ widening</Pill>} {r.summary}
        </div>
      ))}
    </div>
  );
}

/**
 * The Spec page: a raw editor with the diff BESIDE it.
 *
 * The diff pane shows draft-vs-applied by default — literally what Apply will
 * do, including the widening flag, before the operator commits to it. That is
 * the whole reason it is live and adjacent rather than a confirmation dialog:
 * a widening should be visible while you are still typing it.
 */
function SpecPage({
  connectionId,
  tenantId,
  specVersion,
  onApplied,
}: {
  connectionId: string;
  tenantId: string;
  specVersion: number;
  onApplied: (connection: any) => void;
}) {
  const api = useApiClient();
  const [draft, setDraft] = useState<string | null>(null);
  const [applied, setApplied] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewingRevision, setViewingRevision] = useState<number | null>(null);
  const { revisions, reload: reloadRevisions } = useProxyVaultRevisions(connectionId);

  useEffect(() => {
    let cancelled = false;
    api
      .exportProxyVaultConnection(connectionId)
      .then((yaml) => {
        if (cancelled) return;
        setApplied(yaml);
        setDraft((current) => current ?? yaml);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [api, connectionId, specVersion]);

  const dirty = draft !== null && draft !== applied;
  const { preview, pending } = useSpecPreview(connectionId, draft ?? '', dirty);

  const apply = async (): Promise<void> => {
    if (draft === null) return;
    setBusy(true);
    setError(null);
    try {
      const next = await api.updateProxyVaultConnection(connectionId, draft, specVersion, tenantId);
      setApplied(draft);
      reloadRevisions();
      onApplied(next);
    } catch (err) {
      setError(describeApiError(err, 'Could not apply the spec'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <div style={{ flex: '1 1 50%', display: 'flex', flexDirection: 'column', borderRight: '1px solid var(--border)' }}>
          <PaneTitle>
            YAML {dirty ? `— editing (draft v${specVersion + 1})` : `— applied v${specVersion}`}
          </PaneTitle>
          <textarea
            value={draft ?? ''}
            onChange={(e) => setDraft(e.target.value)}
            spellCheck={false}
            data-testid="pv-spec-editor"
            style={{
              flex: 1,
              border: 0,
              outline: 'none',
              resize: 'none',
              padding: '10px 16px',
              background: 'var(--bg)',
              color: 'var(--text)',
              fontFamily: mono,
              fontSize: 12,
              lineHeight: 1.55,
            }}
          />
        </div>

        <div style={{ flex: '1 1 50%', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <PaneTitle>
            {viewingRevision === null
              ? `Diff — draft vs v${specVersion}${pending ? ' …' : ''}`
              : `Diff — v${viewingRevision} → v${viewingRevision + 1}`}
          </PaneTitle>
          <div style={{ flex: 1, overflow: 'auto', padding: '10px 16px' }} data-testid="pv-spec-diff">
            {viewingRevision === null && !dirty && (
              <div style={{ fontSize: 12, color: 'var(--text-mute)' }}>
                No unapplied changes. Edit the document and this shows exactly what Apply will do.
              </div>
            )}
            {viewingRevision === null && preview && dirty && (
              <>
                {preview.widened && (
                  <div
                    data-testid="pv-widening"
                    style={{
                      marginBottom: 10,
                      padding: 8,
                      borderRadius: 6,
                      background: 'rgba(183,121,31,0.12)',
                      color: '#b7791f',
                      fontSize: 11,
                    }}
                  >
                    ⚠ widening — this revision lets the credential reach{' '}
                    {preview.widened_paths.join(', ')}. It is allowed, and the audit row says so
                    forever.
                  </div>
                )}
                <Issues items={preview.errors} tone="error" label="Invalid" />
                <Issues
                  items={preview.conflicts.map((c) => ({ path: c.reason, message: c.detail }))}
                  tone="error"
                  label="Conflict"
                />
                <Issues items={preview.not_yet_enabled} tone="warn" label="Not yet enabled" />
                <Issues items={preview.warnings} tone="warn" label="Warning" />
                <Diff text={preview.diff} />
              </>
            )}
            {viewingRevision !== null && <RevisionDiff connectionId={connectionId} from={viewingRevision} />}
          </div>
        </div>
      </div>

      {error && (
        <div
          data-testid="pv-spec-error"
          style={{
            padding: '8px 16px',
            background: 'rgba(192,57,43,0.08)',
            color: '#c0392b',
            fontSize: 11,
            fontFamily: mono,
            whiteSpace: 'pre-wrap',
          }}
        >
          {error}
        </div>
      )}

      <div
        style={{
          display: 'flex',
          gap: 8,
          alignItems: 'center',
          padding: '10px 16px',
          borderTop: '1px solid var(--border)',
          flexWrap: 'wrap',
        }}
      >
        <button
          type="button"
          onClick={() => void apply()}
          disabled={!dirty || busy}
          style={{ ...primaryButton, opacity: !dirty || busy ? 0.5 : 1 }}
          data-testid="pv-spec-apply"
        >
          {busy ? 'Applying…' : `Apply as v${specVersion + 1}`}
        </button>
        <button type="button" onClick={() => setDraft(applied)} disabled={!dirty} style={secondaryButton}>
          Discard
        </button>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 10, color: 'var(--text-mute)' }}>Revisions:</span>
          {revisions.slice(0, 8).map((r) => (
            <button
              key={r.version}
              type="button"
              onClick={() => setViewingRevision(viewingRevision === r.version - 1 ? null : r.version - 1)}
              style={{
                ...secondaryButton,
                padding: '3px 8px',
                fontSize: 10,
                ...(r.security_relevant && { borderColor: '#b7791f', color: '#b7791f' }),
              }}
              title={r.summary}
            >
              v{r.version}
              {r.security_relevant ? ' ⚠' : ''}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function RevisionDiff({ connectionId, from }: { connectionId: string; from: number }) {
  const api = useApiClient();
  const [diff, setDiff] = useState<string>('');

  useEffect(() => {
    let cancelled = false;
    api
      .diffProxyVaultRevisions(connectionId, from, from + 1)
      .then((res) => {
        if (!cancelled) setDiff(res.diff);
      })
      .catch(() => {
        if (!cancelled) setDiff('This revision has no successor to diff against.');
      });
    return () => {
      cancelled = true;
    };
  }, [api, connectionId, from]);

  return <Diff text={diff} />;
}

function Diff({ text }: { text: string }) {
  return (
    <pre style={{ fontFamily: mono, fontSize: 11.5, lineHeight: 1.5, margin: 0 }}>
      {text.split('\n').map((line, i) => (
        <div
          key={i}
          style={{
            color: line.startsWith('+')
              ? '#2f855a'
              : line.startsWith('-')
                ? '#c0392b'
                : 'var(--text-soft)',
            background: line.startsWith('+')
              ? 'rgba(47,133,90,0.08)'
              : line.startsWith('-')
                ? 'rgba(192,57,43,0.08)'
                : 'transparent',
            whiteSpace: 'pre-wrap',
          }}
        >
          {line || ' '}
        </div>
      ))}
    </pre>
  );
}

function Issues({
  items,
  tone,
  label,
}: {
  items: Array<{ path: string; message: string }>;
  tone: 'error' | 'warn';
  label: string;
}) {
  if (items.length === 0) return null;
  const color = tone === 'error' ? '#c0392b' : '#b7791f';
  return (
    <div style={{ marginBottom: 10 }} data-testid={`pv-issues-${tone}`}>
      {items.map((item, i) => (
        <div key={i} style={{ fontSize: 11, color, marginBottom: 3 }}>
          <span style={{ fontFamily: mono }}>
            {label}: {item.path}
          </span>{' '}
          — {item.message}
        </div>
      ))}
    </div>
  );
}

function PaneTitle({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        padding: '8px 16px',
        fontSize: 10,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        color: 'var(--text-mute)',
        borderBottom: '1px solid var(--border-soft)',
      }}
    >
      {children}
    </div>
  );
}
